import { CopyToClipboardButton } from '@/components/Patient/components/CopyToClipboardButton';
import { zodResolver } from '@hookform/resolvers/zod';
import { PhoneNumberTextField } from '@montugroup/design-system';
import type { GetPatientDetailsInclude } from '@montugroup/pms-api-contracts';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import type { TextFieldProps } from '@mui/material';
import { Grid, styled, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import InputAdornment from '@mui/material/InputAdornment';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import type { z } from 'zod';
import FormSection from '../../FormSection';
import { PATIENT_DETAILS_FORM_NAMES } from '../../Patient.constants';
import { schema } from './ContactDetails.schema';

const sharedPhoneNumberTextFieldProps: TextFieldProps = {
  name: 'phone',
  label: 'Phone',
  fullWidth: true,
  InputProps: {
    startAdornment: (
      <InputAdornment position="start">
        <PhoneIphoneIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
      </InputAdornment>
    ),
  },
};

export type ContactDetailsFormFields = z.infer<typeof schema>;

export type ContactDetailsProps = {
  editable?: boolean;
  isEmailEditable?: boolean;
  defaultValues?: ContactDetailsFormFields;
  maskedPhoneNumber?: string;
  onSubmit?: (data: ContactDetailsFormFields) => void;
  isLoading?: boolean;
};

// This styled wrapper mimics the disabled input style. This is used because during renders
// the 'disabled' prop on the email <TextFieldElement> was causing the field to switch to
// 'isDirty=true' even when the value never changed. This had knock-on effects with the
// 'Unsaved changes' text.
const StyledEmailInput = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isEmailDisabled',
})<{ isEmailDisabled: boolean }>(({ isEmailDisabled }) => ({
  ...(isEmailDisabled && {
    opacity: 0.7,
  }),
}));

export const ContactDetails = ({
  editable,
  isEmailEditable = false,
  defaultValues,
  maskedPhoneNumber,
  onSubmit,
  isLoading,
}: ContactDetailsProps) => {
  const FORM_NAME: GetPatientDetailsInclude = PATIENT_DETAILS_FORM_NAMES.PATIENT_CONTACT_DETAILS;

  const formContext = useForm<ContactDetailsFormFields>({
    defaultValues,
    values: defaultValues,
    resolver: zodResolver(schema),
  }) as ReturnType<typeof useForm<ContactDetailsFormFields>>;

  const {
    control,
    formState: { errors, isDirty },
  } = formContext;

  const isEmailDisabled = !editable || !isEmailEditable;

  const isEffectivelyEditable = editable && typeof onSubmit === 'function';

  const handleOnSubmit = (formData: ContactDetailsFormFields) => {
    if (onSubmit && isDirty) {
      onSubmit(formData);
    }
  };

  const transform = (dirtyValues: FieldValues): ContactDetailsFormFields => {
    return {
      email: dirtyValues?.email,
      phone: dirtyValues?.phone === null ? '' : dirtyValues?.phone,
    } as ContactDetailsFormFields;
  };

  return (
    <FormSection
      title="Contact Details"
      formName={FORM_NAME}
      formContext={formContext}
      onSubmit={handleOnSubmit}
      isLoading={isLoading}
      transform={transform}
    >
      <FormContainer formContext={formContext}>
        <Grid container columnSpacing={10} rowSpacing={5} marginY={2} data-testid="contact-details-form">
          <Grid item xs={12} sm={12} lg={6}>
            <StyledEmailInput isEmailDisabled={isEmailDisabled}>
              <TextFieldElement
                name="email"
                label="Email *"
                fullWidth
                InputProps={{
                  readOnly: isEmailDisabled,
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon sx={{ color: (theme) => theme.palette.grey[500] }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyToClipboardButton
                        text={formContext.getValues('email')}
                        label="Copy email"
                        tooltipTitle="Copy email"
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </StyledEmailInput>
          </Grid>
          <Grid item xs={12} sm={12} lg={6}>
            {maskedPhoneNumber ? (
              <TextField {...sharedPhoneNumberTextFieldProps} disabled value={maskedPhoneNumber} />
            ) : (
              <PhoneNumberTextField
                {...sharedPhoneNumberTextFieldProps}
                control={control}
                helperText={errors.phone?.message}
                variant={'outlined'}
                disabled={!isEffectivelyEditable}
              />
            )}
          </Grid>
        </Grid>
      </FormContainer>
    </FormSection>
  );
};
